/**
 * LinkedIn URL Types
 *
 * Branded types for LinkedIn profile URLs with format validation.
 *
 * Supported profile types:
 * - /in/ - Personal profiles
 * - /company/ - Company pages
 * - /school/ - Educational institutions
 * - /showcase/ - Showcase pages (company sub-pages)
 * - /group/ - LinkedIn groups
 * - /pulse/ - Published articles
 * - unknown - Any other valid LinkedIn URL
 *
 * NOTE: Branded types intentionally don't use the "Type" suffix as they
 * represent values with specific semantic meaning, not plain type aliases.
 *
 * @example
 * ```typescript
 * // Creating LinkedIn URLs with validation
 * const url = LinkedInUrl.tryCreate('https://linkedin.com/in/johndoe'); // LinkedInUrl | undefined
 *
 * // Personal LinkedIn URL (restricted to /in/ only)
 * const personalUrl = PersonalLinkedInUrl.tryCreate('https://linkedin.com/in/johndoe');
 *
 * // Type-safe usage - compiler knows the constraint
 * function enrichFromLinkedIn(profileUrl: LinkedInUrl): void {
 *   // profileUrl is guaranteed to be valid LinkedIn URL format
 * }
 * ```
 */

import { z } from "zod";

export const LinkedInUrlSchema = z
  .string()
  .refine((val) => isValidLinkedInUrl(val), {
    message: "Invalid LinkedIn URL format",
  })
  .brand("LinkedInUrl");

/**
 * Branded type for validated LinkedIn profile URLs.
 *
 * Validation rules:
 * - Must be a valid URL with http or https protocol
 * - Domain must be linkedin.com or www.linkedin.com
 * - Must have a valid path structure
 *
 * Normalization:
 * - Entire URL lowercased
 * - Protocol normalized to https
 * - Trailing slashes removed
 *
 * Used for: LinkedIn profile enrichment lookups
 */

export type LinkedInUrl = z.infer<typeof LinkedInUrlSchema>;

// Path segment accepts Unicode letters/digits so URLs with accented names
// (e.g. linkedin.com/in/maría-julia) validate without prior percent-encoding.
const LINKEDIN_URL_KNOWN_TYPES_REGEX =
  /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/(in|company|school|showcase|group|pulse)\/([\p{L}\p{N}_-]|%[0-9a-f]{2})+(\/[a-z]{2})?\/?$/iu;

/**
 * LinkedIn URL regex pattern for any valid LinkedIn URL (fallback for unknown types) Matches any valid linkedin.com URL with a path, including URL-encoded characters and country subdomains (e.g., ar.linkedin.com, br.linkedin.com)
 */
const LINKEDIN_URL_FALLBACK_REGEX =
  /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/([\p{L}\p{N}_/-]|%[0-9a-f]{2})+$/iu;

/**
 * Normalize a LinkedIn URL.
 * - Trims whitespace
 * - Lowercases entire URL
 * - Adds https:// if no protocol present
 * - Converts http to https
 * - Strips query string and fragment
 * - Removes trailing slash
 */
function normalizeLinkedInUrl(value: string): string {
  let normalized = value.trim().toLowerCase();

  // Add https:// if no protocol present (e.g., "linkedin.com/in/username")
  if (!normalized.startsWith("http://") && !normalized.startsWith("https://")) {
    normalized = "https://" + normalized;
  }

  if (normalized.startsWith("http://")) {
    normalized = "https://" + normalized.slice(7);
  }

  try {
    const u = new URL(normalized);
    u.search = "";
    u.hash = "";
    normalized = u.href.toLowerCase();
  } catch {
    // If URL parsing fails, return as-is (validation will reject it)
  }

  if (normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

function isValidLinkedInUrl(value: string): boolean {
  if (typeof value !== "string") {
    return false;
  }

  return (
    LINKEDIN_URL_KNOWN_TYPES_REGEX.test(value) ||
    LINKEDIN_URL_FALLBACK_REGEX.test(value)
  );
}

export const LinkedInUrl = {
  unsafeCreate(value: string): LinkedInUrl {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalizeLinkedInUrl(value) as LinkedInUrl;
  },

  isValid(value: string): boolean {
    return isValidLinkedInUrl(value);
  },

  /**
   * Parse a value as LinkedInUrl or return undefined if invalid.
   * @param value - String to parse
   * @returns LinkedInUrl if valid (normalized), undefined otherwise
   */
  tryCreate(value: string): LinkedInUrl | undefined {
    const normalized = normalizeLinkedInUrl(value);
    if (!isValidLinkedInUrl(normalized)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as LinkedInUrl;
  },
} as const;

export const PersonalLinkedInUrlSchema = z
  .string()
  .refine((val) => isValidPersonalLinkedInUrl(val), {
    message: "Invalid personal LinkedIn URL format",
  })
  .brand("PersonalLinkedInUrl");

/**
 * Branded type for validated personal LinkedIn profile URLs.
 *
 * Restricted to /in/ paths only (personal profiles).
 * Used when only personal LinkedIn profiles are acceptable.
 *
 * Validation rules:
 * - Must be a valid URL with http or https protocol
 * - Domain must be linkedin.com or www.linkedin.com
 * - Path must be /in/ (personal profile only)
 * - Username must be non-empty
 *
 * Normalization:
 * - Entire URL lowercased
 * - Protocol normalized to https
 * - Trailing slashes removed
 */

export type PersonalLinkedInUrl = z.infer<typeof PersonalLinkedInUrlSchema>;

const PERSONAL_LINKEDIN_URL_REGEX =
  /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/([\p{L}\p{N}_-]|%[0-9a-f]{2})+(\/[a-z]{2})?\/?$/iu;

function isValidPersonalLinkedInUrl(value: string): boolean {
  return typeof value === "string" && PERSONAL_LINKEDIN_URL_REGEX.test(value);
}

export const PersonalLinkedInUrl = {
  unsafeCreate(value: string): PersonalLinkedInUrl {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalizeLinkedInUrl(value) as PersonalLinkedInUrl;
  },

  isValid(value: string): boolean {
    return isValidPersonalLinkedInUrl(value);
  },

  /**
   * Parse a value as PersonalLinkedInUrl or return undefined if invalid.
   * @param value - String to parse
   * @returns PersonalLinkedInUrl if valid (normalized), undefined otherwise
   */
  tryCreate(value: string): PersonalLinkedInUrl | undefined {
    const normalized = normalizeLinkedInUrl(value);

    if (!isValidPersonalLinkedInUrl(normalized)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as PersonalLinkedInUrl;
  },
} as const;
